import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { api } from '../services/api';
import { defaultPeriod } from '../services/defaultPeriod';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import AdminTabs       from '../components/Shared/AdminTabs';
import PageHeader from '../components/Shared/PageHeader';
import { c, t } from '../theme';

// Amber for "at risk" in bars. theme.js has no mid-lightness amber -- its
// warning is a text colour -- and a bar needs one that sits visibly between
// the green and the red.
const RISK_FILL = '#EAB308';

// What a review's status means to someone reading the board. "Data Loaded" was
// the database's word for it.
const REVIEW_WORDS = {
  pending:      'No data yet',
  data_loaded:  'Review not started',
  in_progress:  'Review in progress',
  submitted:    'Submitted',
  acknowledged: 'Submitted',
};

// The company-wide MOR board. It leads with how the yards performed against
// budget; whether reviews are in is one quiet line under the title, because a
// month where nobody reviews is a real and likely state, and a big "0 / 8" made
// the page about that rather than about the numbers.
export default function CompanyOverview() {
  const { periodId } = useParams();
  const navigate     = useNavigate();

  const { data: periods } = useQuery({ queryKey: ['periods'], queryFn: () => api.get('/periods') });
  const { data, isLoading, isError } = useQuery({
    queryKey: ['company-dashboard', periodId],
    queryFn:  () => api.get(`/admin/company-dashboard/${periodId}`),
    enabled:  !!periodId,
  });

  if (!periodId) return <AutoRedirect />;
  if (isLoading)  return <LoadingSpinner />;
  if (isError)    return <div style={{ padding: 40, color: c.danger }}>Failed to load dashboard.</div>;

  const { period, costCenters, companyRollup } = data;
  const good    = num(companyRollup.total_good);
  const risk    = num(companyRollup.total_risk);
  const off     = num(companyRollup.total_off);
  const total   = num(companyRollup.total_metrics);
  const scored  = good + risk + off;
  const submitted = costCenters.filter((y) => y.status === 'submitted' || y.status === 'acknowledged').length;
  const worst = [...costCenters].sort((a, b) => num(b.off_count) - num(a.off_count));
  const maxOff = Math.max(1, ...costCenters.map((y) => num(y.off_count)));

  return (
    <div style={s.page}>
      <style>{css}</style>
      <AdminTabs />

      <PageHeader
        eyebrow={`Company-wide · ${costCenters.length} yards`}
        title={`Monthly Operations Review — ${period.label}`}
        alignActions="end"
        meta={
          <span style={s.quiet}>
            Reviews: {submitted === 0 ? 'none submitted yet' : `${submitted} of ${costCenters.length} submitted`}
          </span>
        }
        actions={periods?.length > 0 && (
          <div style={s.field}>
            <label htmlFor="mor-period" style={s.fieldLabel}>Period</label>
            <select
              id="mor-period"
              style={s.select}
              value={periodId}
              onChange={(e) => navigate(`/admin/company/${e.target.value}`)}
            >
              {periods.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
        )}
      />

      <div className="co-top">
        <section style={s.panel} className="co-health">
          <div style={s.panelLabel}>Metric health · {scored} of {total} metrics scored</div>
          <div style={s.healthNums}>
            <HealthNum value={good} label="on target" pctOf={scored} color={c.successText} />
            <HealthNum value={risk} label="at risk"   pctOf={scored} color={c.warning} />
            <HealthNum value={off}  label="off track" pctOf={scored} color={c.dangerText} />
          </div>
          {scored > 0 && <StackBar good={good} risk={risk} off={off} height={10} />}
          {total - scored > 0 && (
            <div style={s.quiet}>
              The other {total - scored} metric{total - scored === 1 ? ' has' : 's have'} no budget to compare against, so {total - scored === 1 ? "it isn't" : "they aren't"} scored.
            </div>
          )}
        </section>

        <section style={s.panel}>
          <div style={s.panelLabel}>Most off track</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {worst.map((y) => (
              <div key={y.id} style={s.rankRow}>
                <span style={s.rankName}>{y.cost_center_name}</span>
                <div style={{ ...s.bar, background: c.canvas }}>
                  <div style={{ width: `${(num(y.off_count) / maxOff) * 100}%`, background: c.danger }} />
                </div>
                <b style={{ textAlign: 'right', ...t.tabular }}>{num(y.off_count)}</b>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div style={s.ccGrid}>
        {costCenters.map((cc) => <CostCenterCard key={cc.id} cc={cc} periodId={periodId} />)}
      </div>
      <div style={{ ...s.quiet, marginTop: 10 }}>Each yard's bar shows its scored metrics only.</div>
    </div>
  );
}

function CostCenterCard({ cc, periodId }) {
  const good = num(cc.good_count);
  const risk = num(cc.risk_count);
  const off  = num(cc.off_count);
  const explained = num(cc.off_explained);
  const reviewWords = cc.status === 'submitted' || cc.status === 'acknowledged'
    ? `Submitted${cc.submitted_by_name ? ` by ${cc.submitted_by_name}` : ''}`
    : (REVIEW_WORDS[cc.status] || 'Review not started');

  return (
    <Link to={`/review/${cc.cost_center_id}/${periodId}`} style={s.ccCard} className="co-card">
      <div style={s.ccTop}>
        <div style={s.ccCode}>{cc.code}</div>
        <div style={s.ccName}>{cc.cost_center_name}</div>
        <ChevronRight size={16} color={c.borderStrong} style={{ marginLeft: 'auto', flexShrink: 0 }} />
      </div>

      {good + risk + off > 0 ? (
        <>
          <StackBar good={good} risk={risk} off={off} height={6} />
          <div style={s.ccStats}>
            <span><b style={{ color: c.successText }}>{good}</b> on target</span>
            <span><b style={{ color: c.warning }}>{risk}</b> at risk</span>
            <span><b style={{ color: c.dangerText }}>{off}</b> off track</span>
          </div>
        </>
      ) : (
        <div style={s.quiet}>No scored metrics this month.</div>
      )}

      <div style={s.ccFoot}>
        <span>{reviewWords}</span>
        {off > 0 && <span>{explained} of {off} explained</span>}
      </div>

      {cc.overall_comment && (
        <div style={s.ccComment}>
          "{cc.overall_comment.slice(0, 100)}{cc.overall_comment.length > 100 ? '…' : ''}"
        </div>
      )}
    </Link>
  );
}

function HealthNum({ value, label, pctOf, color }) {
  return (
    <div>
      <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', color, ...t.tabular }}>{value}</div>
      <div style={{ fontSize: 12.5, color: c.textStrong }}>
        {label}{pctOf > 0 ? ` · ${Math.round((value / pctOf) * 100)}%` : ''}
      </div>
    </div>
  );
}

function StackBar({ good, risk, off, height }) {
  const total = good + risk + off;
  const pct = (n) => `${(n / total) * 100}%`;
  return (
    <div style={{ ...s.bar, height }} role="img" aria-label={`${good} on target, ${risk} at risk, ${off} off track`}>
      <div style={{ width: pct(good), background: c.success }} />
      <div style={{ width: pct(risk), background: RISK_FILL }} />
      <div style={{ width: pct(off),  background: c.danger }} />
    </div>
  );
}

function num(v) { return parseInt(v, 10) || 0; }

// Default landing for the board: the previous calendar month's period, else
// the most recent one, else the periods list so an admin can create one.
function AutoRedirect() {
  const navigate = useNavigate();
  const { data: periods, isError } = useQuery({
    queryKey: ['periods'],
    queryFn:  () => api.get('/periods'),
  });

  useEffect(() => {
    if (!periods) return;
    const target = defaultPeriod(periods);
    navigate(target ? `/admin/company/${target.id}` : '/admin', { replace: true });
  }, [periods]);

  if (isError) return (
    <div style={{ padding: 40, color: c.danger }}>Failed to load periods.</div>
  );
  return <LoadingSpinner />;
}

const css = `
.co-top { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin-bottom: 14px; }
.co-health { grid-column: span 2; }
.co-card { transition: border-color 0.15s, box-shadow 0.15s; }
.co-card:hover { border-color: #94A3B8; box-shadow: 0 1px 3px rgba(17,24,39,0.06); }
@media (max-width: 900px) {
  .co-top { grid-template-columns: minmax(0, 1fr); }
  .co-health { grid-column: auto; }
}
`;

const s = {
  page:       { maxWidth: '1200px', margin: '0 auto' },
  quiet:      { fontSize: '12.5px', color: c.textMuted },
  field:      { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldLabel: { fontSize: '11px', fontWeight: 600, color: c.textMuted },
  select:     { font: 'inherit', fontSize: '13px', padding: '7px 10px', minWidth: 170, border: `1px solid ${c.borderStrong}`, borderRadius: 6, background: c.surface, color: c.text },
  panel:      { padding: '18px 20px', background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 12 },
  panelLabel: { ...t.label, color: c.textMuted },
  healthNums: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 },
  bar:        { display: 'flex', height: 6, borderRadius: 4, overflow: 'hidden', background: c.border },
  rankRow:    { display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr) 22px', gap: 8, alignItems: 'center', fontSize: '12.5px', color: c.text },
  rankName:   { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  ccGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12 },
  ccCard:     { display: 'flex', flexDirection: 'column', gap: 11, textDecoration: 'none', color: c.text, background: c.surface, border: `1px solid ${c.border}`, borderRadius: 10, padding: 16 },
  ccTop:      { display: 'flex', alignItems: 'center', gap: 10 },
  ccCode:     { width: 36, height: 36, borderRadius: 8, background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#475569', flexShrink: 0 },
  ccName:     { fontSize: '14px', fontWeight: 600 },
  ccStats:    { display: 'flex', gap: 10, fontSize: '12px', color: c.textStrong },
  ccFoot:     { display: 'flex', justifyContent: 'space-between', gap: 8, paddingTop: 10, borderTop: `1px solid ${c.canvas}`, fontSize: '12px', color: c.textMuted },
  ccComment:  { fontSize: '11px', color: c.textMuted, fontStyle: 'italic', lineHeight: 1.5 },
};
